import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, BookOpen, Phone, Calendar, Wallet, MapPin, ChevronRight, ClipboardCheck,
} from "lucide-react";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import TutorMobileLayout, { MobileSkeletonCard } from "../components/tutor/TutorMobileLayout";

/**
 * V25 Phase 5 — Tutor Tuitions list (mobile-first).
 * Lists all active + paused ongoing tuitions for this tutor. Each card opens
 * its attendance view on tap.
 */
export default function TutorTuitions() {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await tutorApi.get("/tutor/me/ongoing-tuitions");
            setItems(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    return (
        <TutorMobileLayout title="Ongoing Tuitions">
            {loading ? (
                <div className="space-y-3">
                    {[...Array(3)].map((_, i) => <MobileSkeletonCard key={i} />)}
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-8 text-center" data-testid="tutor-tuitions-empty">
                    <BookOpen className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <div className="font-semibold text-slate-700">No active tuitions yet</div>
                    <p className="text-xs text-slate-500 mt-1">
                        Approved demos automatically activate here. Pick up demos from the Leads tab to get started.
                    </p>
                    <Link to="/tutor/leads" className="inline-block mt-3 text-xs font-bold text-[#1E3A8A]">Browse leads →</Link>
                </div>
            ) : (
                <div className="space-y-3" data-testid="tutor-tuitions-list">
                    {items.map((o) => (
                        <Link key={o.id} to={`/tutor/tuitions/${o.id}/attendance`} data-testid={`tutor-tuition-card-${o.id}`}
                            className="block bg-white border border-slate-200 rounded-2xl p-4 shadow-sm hover:shadow active:scale-[0.98] transition">
                            <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                    {(o.student_name || "S").slice(0, 2).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-[#0F172A] truncate">{o.student_name}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{o.grade} · {o.board} · {o.subjects}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-3 text-[11px] text-slate-600">
                                <div className="flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" /><span className="truncate">{o.schedule}</span></div>
                                <div className="flex items-center gap-1"><Wallet className="w-3 h-3 text-slate-400" />₹{o.monthly_fee?.toLocaleString?.()}/mo</div>
                                <div className="flex items-center gap-1 col-span-2"><MapPin className="w-3 h-3 text-slate-400" /><span className="truncate">{o.area}, {o.city}</span></div>
                                <div className="flex items-center gap-1 col-span-2"><Phone className="w-3 h-3 text-slate-400" />{o.parent_phone}</div>
                            </div>
                            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                                <ClipboardCheck className="w-3 h-3" /> Tap to manage attendance
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </TutorMobileLayout>
    );
}
